import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { PlanTiers } from "@/react/helpers/planHelpers";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import { supportedCurrencies } from "@circle-react/config/Currencies";
import { useFetchEmailHubPlan } from "./hooks/useFetchEmailHubPlan";
import { useFetchPlan } from "./hooks/useFetchPlan";
import type { EmailHubPlan, UpgradePlan } from "./types";

export interface UpgradePlanContextValue {
  billingCurrency?: string;
  canCheckoutInstantly?: boolean;
  couponId?: any;
  emailHubPlan: EmailHubPlan;
  errorFetchingEmailHubPlan?: unknown;
  errorFetchingPlan?: unknown;
  isDoubleUpgrade: boolean;
  isEmailHubOnlyUpgrade: boolean;
  isEmailHubPlanLoading: boolean;
  isEmailHubRequired?: boolean;
  isPlanLoading: boolean;
  isRenewal?: boolean;
  isUpdatingCommunityHubPlan: boolean;
  plan: UpgradePlan;
  setBillingCurrency: (currency: string) => void;
  shouldShowCurrencySelection: boolean;
  shouldSkipProrationStep?: boolean;
  showPlanDetails: boolean;
  source?: string;
  usedForWorkflows?: boolean;
}

const UpgradePlanContext = createContext<UpgradePlanContextValue | null>(null);
UpgradePlanContext.displayName = "UpgradePlanContext";

export const useUpgradePlanContext = () => {
  const ctx = useContext(UpgradePlanContext);
  if (!ctx)
    throw new Error(
      "useUpgradePlanContext must be used within <UpgradePlanProvider>",
    );
  return ctx;
};

export interface UpgradePlanProviderProps {
  children: ReactNode;
  couponId?: string;
  isEmailHubRequired?: boolean;
  isRenewal?: boolean;
  planId?: any;
  planTier?: PlanTiers;
  shouldSkipProrationStep?: boolean;
  showPlanDetails?: boolean;
  source?: string;
  usedForWorkflows?: boolean;
}

export const UpgradePlanProvider = ({
  children,
  planId,
  planTier,
  couponId,
  source,
  usedForWorkflows = false,
  showPlanDetails = false,
  shouldSkipProrationStep = false,
  isEmailHubRequired = false,
  isRenewal = false,
}: UpgradePlanProviderProps) => {
  const { canChooseCurrency, defaultCurrency, shouldShowProration } =
    usePlanStatus();

  const [billingCurrency, setBillingCurrencyState] = useState<
    string | undefined
  >(defaultCurrency ?? undefined);
  const [hasUserChangedCurrency, setHasUserChangedCurrency] = useState(false);
  const [fetchedPlanTier, setFetchedPlanTier] = useState<string | undefined>();

  useEffect(() => {
    if (!hasUserChangedCurrency)
      setBillingCurrencyState(defaultCurrency ?? undefined);
  }, [defaultCurrency, hasUserChangedCurrency]);

  const locale = useMemo(
    () =>
      new URLSearchParams(window.location.search).get("locale") ?? undefined,
    [],
  );

  const fetchParams = useMemo(
    () => ({
      planTier: hasUserChangedCurrency ? fetchedPlanTier : planTier,
      planId: hasUserChangedCurrency ? undefined : planId,
      billingCurrency,
      locale,
      isEmailHubRequired,
    }),
    [
      hasUserChangedCurrency,
      fetchedPlanTier,
      planTier,
      planId,
      billingCurrency,
      locale,
      isEmailHubRequired,
    ],
  );

  const emailHubFetchParams = useMemo(
    () => ({
      billingCurrency,
      locale,
      isEnabled: isEmailHubRequired,
    }),
    [billingCurrency, locale, isEmailHubRequired],
  );

  const { plan, isPlanLoading, errorFetchingPlan } = useFetchPlan(fetchParams);
  const { emailHubPlan, isEmailHubPlanLoading, errorFetchingEmailHubPlan } =
    useFetchEmailHubPlan(emailHubFetchParams);

  const canCheckoutInstantly = useMemo(() => {
    if (!plan) return false;
    const isProrationShown =
      shouldShowProration(plan) && !shouldSkipProrationStep;
    return !showPlanDetails && !isProrationShown && !isRenewal;
  }, [
    plan,
    showPlanDetails,
    shouldShowProration,
    shouldSkipProrationStep,
    isRenewal,
  ]);

  const isEmailHubOnlyUpgrade = Boolean(
    isEmailHubRequired && !plan && emailHubPlan && !isEmailHubPlanLoading,
  );

  const isDoubleUpgrade = Boolean(plan && isEmailHubRequired && emailHubPlan);

  const shouldShowCurrencySelection = Boolean(
    canChooseCurrency &&
      (billingCurrency === supportedCurrencies.BRL.code.toLowerCase() ||
        hasUserChangedCurrency),
  );

  useEffect(() => {
    if (!isPlanLoading && plan) {
      if (!fetchedPlanTier) setFetchedPlanTier(plan.tier);

      if (!canCheckoutInstantly) setBillingCurrencyState(plan.currency);
    }
  }, [plan, isPlanLoading, fetchedPlanTier, canCheckoutInstantly]);

  const handleSetBillingCurrency = useCallback((currency: string) => {
    setBillingCurrencyState(currency);
    setHasUserChangedCurrency(true);
  }, []);

  const isUpdatingCommunityHubPlan = Boolean(plan);
  const value: UpgradePlanContextValue = useMemo(
    () => ({
      billingCurrency,
      canCheckoutInstantly,
      couponId,
      plan,
      marketingHubPlan: null,
      emailHubPlan,
      isPlanLoading,
      isEmailHubPlanLoading,
      errorFetchingPlan,
      errorFetchingEmailHubPlan,
      setBillingCurrency: handleSetBillingCurrency,
      shouldShowCurrencySelection,
      showPlanDetails,
      shouldSkipProrationStep,
      source,
      usedForWorkflows,
      isEmailHubRequired,
      isEmailHubOnlyUpgrade,
      isDoubleUpgrade,
      isUpdatingCommunityHubPlan,
      isRenewal,
    }),
    [
      billingCurrency,
      canCheckoutInstantly,
      couponId,
      plan,
      emailHubPlan,
      isPlanLoading,
      isEmailHubPlanLoading,
      errorFetchingPlan,
      errorFetchingEmailHubPlan,
      handleSetBillingCurrency,
      shouldShowCurrencySelection,
      showPlanDetails,
      shouldSkipProrationStep,
      source,
      usedForWorkflows,
      isEmailHubRequired,
      isEmailHubOnlyUpgrade,
      isDoubleUpgrade,
      isUpdatingCommunityHubPlan,
      isRenewal,
    ],
  );

  return (
    <UpgradePlanContext.Provider value={value}>
      {children}
    </UpgradePlanContext.Provider>
  );
};
